import React from "react";
import { Link } from "react-router-dom";
import { Heart, Users, Globe, Play } from "lucide-react";
import { HERO_BG, ABOUT_PASTORS_CAROUSEL, PageHero, AutoCarousel, ExpandableBio } from "../shared";

const AboutPage = () => {
  const leadership = [
    { id: "senior-pastors", name: "Pastors Eddie & Yadira Barragan", role: "Senior Pastors", image: "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/422wpxsv_IMG_9703.jpeg", bio: [
      "Pastors Eddie & Yadira Barragan are the Senior Pastors of World Harvest Christian Outreach in Lancaster, California, where they have faithfully served in ministry for over 20 years of service. With a strong prophetic and apostolic thrust, they are committed to seeing reformation in their city and across the nation as they boldly exalt the mighty and only name of Jesus.",
      "Their leadership is marked by a deep pursuit of God's presence, anchored in \"If we have His presence, we have everything.\" They have cultivated a Kingdom culture rooted in prayer, marked by strong spiritual governance, and centered fully on Jesus and His glory.",
      "Through their ministry, they are passionate about raising up revivalists who seek the face of God and carry His power into every sphere of influence. Their vision is clear: making disciples, raising up leaders, and sending them to the nations—equipping a generation to walk in truth, authority, and Kingdom purpose."
    ]},
    { id: "elder-beatrice", name: "Dr. Beatrice Murray", role: "Elder • Church Administrator & CFO", image: "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/l9q8sz50_image.png", bio: "Elder Dr. Beatrice Murray brings wisdom, grace, and years of experience to our leadership team. She carries a Doctorate in Divinity, and her background in ministry allows her to care for the whole person — spirit, soul, and body. She faithfully serves WHCO as our Church Administrator and CFO." },
    { id: "ministers-lloyd", name: "Ministers Nate & Christina Lloyd", role: "Ministers & Licensed Marriage & Family Therapists", image: "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/4d2iyyex_6C9E7FD7-B44E-49C8-B90C-62F2A14B5624.png", bio: "Ministers Nate and Christina Lloyd are passionate in their love for Jesus and seeing the minds of people and marriages restored. As both Licensed Marriage & Family Therapists and ordained ministers, they bring a unique blend of clinical expertise and spiritual depth to help people experience wholeness in Christ. They serve faithfully in counseling, teaching, and mentoring at WHCO." },
    { id: "minister-sabrina", name: "Minister Sabrina Carrillo", role: "Minister", image: "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/kahm82dd_83b82b7c-6823-4ebd-9f2c-1ba99e541aa7.jpeg", bio: "Minister Sabrina Carrillo has a heart for evangelism and discipleship. Her vibrant faith and genuine love for people make her a beloved member of our ministry team. She serves in our women's ministry, leads our youth ministry, and serves as a leader in our young adults ministry, helping to raise and equip the next generation of believers. In addition, she helps coordinate our outreach programs throughout Lancaster, demonstrating a passion for reaching people with the love and hope of Jesus Christ." },
    { id: "minister-candice", name: "Minister Candice James", role: "Minister", image: "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/cygw3v9a_image.png", bio: "Minister Candice James serves with creativity, energy, and love. Her gift for teaching and connecting with people has created a dynamic environment where every generation can grow in their walk with Jesus. She believes every person has a purpose and calling." },
    { id: "ministers-training", name: "Ministers in Training", role: "Emerging Leaders", image: "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts/dmqhgv05_image.jpeg", bio: "Our Ministers in Training program develops the next generation of leaders at WHCO. These emerging ministers are being equipped through mentorship, hands-on ministry experience, and biblical training. They represent the future of our church and are passionate about serving God and His people." },
  ];

  return (
    <div className="pt-20 fade-in">
      <PageHero eyebrow="Our Story" title={<>About<br /><span className="italic">World Harvest</span></>} bg={HERO_BG.about} testId="about-title" />

      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <AutoCarousel images={ABOUT_PASTORS_CAROUSEL} alt="Pastors Eddie and Yadira Barragan" testId="about-pastors-carousel" intervalMs={5000} />
            </div>
            <div>
              <p className="heading-caps text-xs text-black/40 mb-4 tracking-[0.2em]">Our Pastors</p>
              <h2 className="text-4xl md:text-5xl mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                Pastors Eddie &<br /><span className="italic">Yadira Barragan</span>
              </h2>
              <p className="text-lg text-black/60 leading-relaxed mb-6">
                Pastors Eddie and Yadira have been serving the Lancaster community for over 20 years.
                Their heart for discipleship and missions has led WHCO to impact lives locally and around the globe.
              </p>
              <p className="text-black/50 leading-relaxed">
                With a passion for seeing believers grow in their faith and step into their calling,
                they lead with vision, integrity, and a deep love for God&apos;s people.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-32 bg-black text-white">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <p className="heading-caps text-xs text-white/40 mb-8 tracking-[0.2em]">Our Mission</p>
          <h2 className="text-3xl md:text-5xl leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            &ldquo;Making Disciples,<br />Raising Up Leaders,<br /><span className="italic">Launching to the Nations&rdquo;</span>
          </h2>
        </div>
      </section>

      <section className="py-32 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-20">
            <p className="heading-caps text-xs text-black/40 mb-4 tracking-[0.2em]">Our Team</p>
            <h2 className="text-4xl md:text-5xl" style={{ fontFamily: 'Playfair Display, serif' }}>
              Leadership & <span className="italic">Ministers</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12" data-testid="leadership-grid">
            {leadership.map((leader) => {
              const isComposite = leader.id === "ministers-lloyd" || leader.id === "ministers-training";
              const longBio = leader.id === "senior-pastors";
              return (
                <div key={leader.id} className="bg-white shadow-lg overflow-hidden group" data-testid={`leader-${leader.id}`}>
                  <div className={`aspect-square overflow-hidden ${isComposite ? "bg-black flex items-center justify-center" : ""}`}>
                    <img src={leader.image} alt={leader.name}
                      className={`w-full h-full ${isComposite ? "object-contain" : "object-cover"} grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105`}
                    />
                  </div>
                  <div className="p-8">
                    <p className="heading-caps text-[10px] text-red-700 mb-2 tracking-[0.2em]">{leader.role}</p>
                    <h3 className="text-xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>{leader.name}</h3>
                    {longBio ? <ExpandableBio paragraphs={leader.bio} testId={leader.id} /> : <p className="text-sm text-black/60 leading-relaxed">{leader.bio}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-20">
            <p className="heading-caps text-xs text-black/40 mb-4 tracking-[0.2em]">What We Believe</p>
            <h2 className="text-4xl md:text-5xl" style={{ fontFamily: 'Playfair Display, serif' }}>Our Values</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
            {[
              { icon: Heart, title: "Love", desc: "We love God and love people unconditionally" },
              { icon: Users, title: "Community", desc: "We grow together in authentic relationships" },
              { icon: Globe, title: "Missions", desc: "We reach our city and the nations" },
              { icon: Play, title: "Worship", desc: "We glorify God in all we do" },
            ].map((value) => (
              <div key={value.title} className="text-center">
                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-6">
                  <value.icon size={24} />
                </div>
                <h3 className="text-xl mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>{value.title}</h3>
                <p className="text-sm text-black/50">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-black text-white">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <h2 className="text-3xl md:text-4xl mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            We&apos;d Love to <span className="italic">Meet You</span>
          </h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">
            Whether you&apos;re new to faith or a seasoned believer, there&apos;s a place for you at World Harvest.
          </p>
          <Link to="/contact" className="btn-primary inline-block">Plan Your Visit</Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
